import { AdminAppEventRow, AdminSortOrder, AdminUserRecordRow } from '@orbit/api-types'
import { useAdminUserAppEvents } from '@hooks/useAdminUserAppEvents'
import React, { useState } from 'react'
import { formatDateTimeWithSeconds } from '../adminFormat'
import { Card, CardTitle, InlineLink } from '../adminDetail.styles'
import {
	DeletedTag,
	Muted,
	PageButton,
	PageStatus,
	Pagination,
	Row,
	StaticRow,
	Table,
	Td,
	Th,
} from '../adminList.styles'
import {
	EventTypeCell,
	FeedHeader,
	FeedNote,
	FeedTableWrap,
	PayloadBlock,
	PayloadPreview,
	RecordLink,
	RecordSummary,
	SortButton,
	SourceTag,
} from './UserAppEvents.styles'
import { appEventLink, feedRowKey, recordPath, recordSummary } from './userFeedView'
import { EmptyNoteInline, EmptyNote } from '@components/EmptyState'

const PAGE_SIZE = 25

/** The device an event came from, as one line: "ios 18.2 · iPhone15,3". */
const formatDevice = (event: AdminAppEventRow): string =>
	[event.platform, event.osVersion, event.deviceModel].filter(Boolean).join(' · ')

/**
 * The app version as the emitter reported it, with the platform's build identifier
 * appended when there is one — Android sends `versionCode`, iOS `buildNumber`.
 */
const formatVersion = (event: AdminAppEventRow): string => {
	const build = event.versionCode ?? event.buildNumber
	return build != null ? `${event.appVersion} (${build})` : event.appVersion
}

/**
 * A record read from the domain tables: no device or version, and a summary that
 * links to the record's admin page instead of a payload to expand.
 */
const RecordRow: React.FC<{ record: AdminUserRecordRow }> = ({ record }) => {
	const summary = recordSummary(record)
	return (
		<StaticRow $deleted={record.isDeleted}>
			<Td>{formatDateTimeWithSeconds(record.timestamp)}</Td>
			<Td>
				<EventTypeCell>{record.eventType}</EventTypeCell>
				<SourceTag>DB</SourceTag>
			</Td>
			<Td>
				<Muted>—</Muted>
			</Td>
			<Td>
				<Muted>—</Muted>
			</Td>
			<Td>
				{summary === '' ? (
					<Muted>—</Muted>
				) : record.link === null ? (
					<RecordSummary>{summary}</RecordSummary>
				) : (
					<InlineLink to={recordPath(record.link)}>
						<RecordSummary>{summary}</RecordSummary>
					</InlineLink>
				)}
				{record.isDeleted && <DeletedTag>Deleted</DeletedTag>}
			</Td>
		</StaticRow>
	)
}

/**
 * The user's timeline: a paginated, sortable table of every `app_events`
 * row attributed to this user, merged with the records they created in the domain
 * tables (tagged "DB") until the apps send events for those. Independent of the
 * admin layout's date filter — this is the whole history — so it owns its own page
 * and sort state, and flipping the sort restarts at page 1 so the newest/oldest end
 * is what you actually land on.
 *
 * An app event with a payload expands in place to pretty-print it; collapsed rows
 * show a truncated one-liner, so a noisy `data` blob never stretches the table.
 */
const UserAppEvents: React.FC<{ userId: number }> = ({ userId }) => {
	const [page, setPage] = useState(1)
	const [sort, setSort] = useState<AdminSortOrder>('desc')
	const [expandedKey, setExpandedKey] = useState<string | null>(null)

	const { data, isLoading, isError } = useAdminUserAppEvents({
		userId,
		page,
		pageSize: PAGE_SIZE,
		sort,
	})

	const toggleSort = () => {
		setSort(current => (current === 'desc' ? 'asc' : 'desc'))
		setPage(1)
		setExpandedKey(null)
	}

	const goToPage = (next: number) => {
		setPage(next)
		setExpandedKey(null)
	}

	return (
		<Card>
			<FeedHeader>
				<CardTitle>Timeline{data ? ` (${data.total.toLocaleString()})` : ''}</CardTitle>
				<SortButton type="button" onClick={toggleSort} disabled={isLoading || isError}>
					{sort === 'desc' ? 'Newest first ↓' : 'Oldest first ↑'}
				</SortButton>
			</FeedHeader>
			<FeedNote>
				App events, plus rows tagged DB for what the apps don&rsquo;t send events for yet: sign-up, trips
				created and joined, and the plans, expenses and files they added. A join the app
				already reported as trip_joined shows once from each.
			</FeedNote>

			{isLoading ? (
				<EmptyNoteInline>Loading timeline…</EmptyNoteInline>
			) : isError || !data ? (
				<EmptyNoteInline>Could not load this user&rsquo;s timeline.</EmptyNoteInline>
			) : data.events.length === 0 ? (
				<EmptyNoteInline>This user has no history.</EmptyNoteInline>
			) : (
				<>
					<FeedTableWrap>
						<Table>
							<thead>
								<tr>
									<Th>Time</Th>
									<Th>Event</Th>
									<Th>Version</Th>
									<Th>Device</Th>
									<Th>Data</Th>
								</tr>
							</thead>
							<tbody>
								{data.events.map(row => {
									const key = feedRowKey(row)
									if (row.source === 'db') return <RecordRow key={key} record={row} />

									const isExpanded = expandedKey === key
									const payload = row.data
									const link = appEventLink(row)
									return (
										<React.Fragment key={key}>
											<Row
												onClick={() => setExpandedKey(isExpanded ? null : key)}
												aria-expanded={isExpanded}
											>
												<Td>{formatDateTimeWithSeconds(row.timestamp)}</Td>
												<Td>
													<EventTypeCell>{row.eventType}</EventTypeCell>
												</Td>
												<Td>{formatVersion(row)}</Td>
												<Td>{formatDevice(row) || <Muted>—</Muted>}</Td>
												<Td>
													{link && (
														<RecordLink
															to={recordPath(link)}
															onClick={event => event.stopPropagation()}
														>
															Open
														</RecordLink>
													)}
													{payload ? (
														<PayloadPreview>{JSON.stringify(payload)}</PayloadPreview>
													) : (
														<Muted>—</Muted>
													)}
												</Td>
											</Row>
											{isExpanded && (
												<tr>
													<Td colSpan={5}>
														{payload ? (
															<PayloadBlock>{JSON.stringify(payload, null, 2)}</PayloadBlock>
														) : (
															<EmptyNote>This event carries no data payload.</EmptyNote>
														)}
													</Td>
												</tr>
											)}
										</React.Fragment>
									)
								})}
							</tbody>
						</Table>
					</FeedTableWrap>

					<Pagination>
						<PageStatus>
							Page {data.page} of {data.totalPages}
						</PageStatus>
						<PageButton disabled={data.page <= 1} onClick={() => goToPage(Math.max(1, page - 1))}>
							Previous
						</PageButton>
						<PageButton disabled={data.page >= data.totalPages} onClick={() => goToPage(page + 1)}>
							Next
						</PageButton>
					</Pagination>
				</>
			)}
		</Card>
	)
}

export default UserAppEvents
